/**
 * SourceGroupNode — custom React Flow node for SDK source-file groups.
 *
 * Collapsed: compact card showing filename + field count.
 * Expanded: bordered box listing child node names with kind-colored pills.
 */

import { memo, type CSSProperties } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { CrispNodeContent } from "./CrispNodeContent";
import type { SourceGroup } from "./dagGrouping";

const KIND_PILL_COLORS: Record<string, string> = {
  dataframe: "#3b82f6",
  series: "#3b82f6",
  parameter: "#22c55e",
  result: "#22c55e",
  coefficient: "#22c55e",
  scalar: "#a855f7",
  cache_entry: "#64748b",
};

export interface SourceGroupData {
  label: string;
  group: SourceGroup;
  expanded: boolean;
  onToggle: () => void;
  [key: string]: unknown;
}

const stackStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  borderRadius: 8,
  border: "1px solid rgba(97,178,156,0.2)",
  pointerEvents: "none",
};

function SourceGroupNodeInner({ data }: NodeProps) {
  const { label, group, expanded, onToggle } = data as unknown as SourceGroupData;

  if (expanded) {
    return (
      <>
        <Handle type="target" position={Position.Top} style={{ opacity: 0, top: 0 }} />
        <CrispNodeContent width="100%" height="100%">
          <div
            style={{
              background: "rgba(30,58,95,0.06)",
              border: "2px dashed rgba(97,178,156,0.35)",
              borderRadius: 12,
              padding: 12,
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
                gap: 8,
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>{label}</span>
              <button
                onClick={onToggle}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 12,
                  padding: "2px 4px",
                  opacity: 0.7,
                  color: "inherit",
                }}
                title="Collapse source group"
              >
                ▴
              </button>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                flex: 1,
                overflow: "auto",
              }}
            >
              {group.nodes.map((n) => (
                <span
                  key={n.id}
                  style={{
                    fontSize: 10,
                    background: KIND_PILL_COLORS[n.kind] ?? "#64748b",
                    color: "#fff",
                    borderRadius: 4,
                    padding: "3px 8px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {n.name || n.path?.split(".").pop() || n.kind}
                </span>
              ))}
            </div>
          </div>
        </CrispNodeContent>
        <Handle type="source" position={Position.Bottom} style={{ opacity: 0, bottom: 0 }} />
      </>
    );
  }

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <CrispNodeContent width="100%" height="100%" style={{ overflow: "visible" }}>
        <div
          style={{ position: "relative", cursor: "pointer", width: "100%" }}
          onClick={onToggle}
        >
          <div style={{ ...stackStyle, transform: "translate(3px, 3px)", background: "rgba(30,58,95,0.25)" }} />
          <div style={{ ...stackStyle, transform: "translate(1.5px, 1.5px)", background: "rgba(30,58,95,0.4)" }} />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </span>
              <span style={{ fontSize: 12, opacity: 0.7 }}>▾</span>
            </div>

            {group.nodes.length <= 6 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 }}>
                {group.nodes.map((n) => (
                  <span
                    key={n.id}
                    style={{
                      fontSize: 9,
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: 3,
                      padding: "1px 4px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {n.name || n.path?.split(".").pop() || n.kind}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </CrispNodeContent>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </>
  );
}

export const SourceGroupNode = memo(SourceGroupNodeInner);
export default SourceGroupNode;
